/**
 * Managed Assets/Accounts component module.
 * @module advisor/components/manage/managed
 *
 */

import React, {useState} from 'react';
import {connect} from 'react-redux';
import {
  Button,
  Column,
  DescriptiveText,
  Dropdown,
  LinkedText,
  LoadingSpinner,
  NumberInput,
  Page,
  Row,
  Table,
  TextArea,
  TextInput,
  Tile,
} from '../../../../shared';
import {_openModal, isEntitled} from '../../../../shared/store';
import PropTypes from 'prop-types';
import debug from 'debug';
import axiosInstance from '../../../../shared/utils/axios';
import {_setNotification} from '../../../../shared/reducers/headerNotification';
import {useLocation, useParams} from 'react-router-dom';
import useSWR from 'swr';
import qs from 'query-string';
import {buildQueryContext, currentLocation, formatter, parseErrorMessage} from '../../../../shared/utils/utils';
import Icon from '../../../../shared/components/Icon';
import {v4 as uuid} from 'uuid';
import history from '../../../../history';
import moment from 'moment';
import DatePicker from '../../../../shared/components/DatePicker';
import {withTheme} from 'styled-components/macro';
import {PORTAL_NAME} from '../../../../config';

const log = debug('circleblack:advisor:manage:accounts:managed');
export const managedAccountValueRender = account => <Row alignText={'right'}
                                                         wrap={false}
                                                         justifySubRow={'right'}>{PORTAL_NAME !== 'widgets'
&& account.sourceType === 'MANAGED'
&& account.isAdvisor
&& <Icon svg={'circleEdit'}
         action={() => history.push(`/manage/accounts/${account.id}/manage`, {backLocation: currentLocation()})}/>
|| null}{formatter('currency')(account.balance)}
</Row>;

const gatherInstitutions = inputValue => axiosInstance.get(
  '/institutions',
  {params: {title: inputValue ? `~/${inputValue}/` : undefined, sort: '+title', limit: 5}}
)
  .then(res => res.data.map(row => ({label: row.title, value: row})));

/**
 * Managed Assets/Accounts component
 * @name Managed
 */
const Managed = ({openModal, setNotification, session, theme}) => {
  // Account ID means editing
  // Client ID means creating
  const {accountID, clientID} = useParams();
  const [saving, setSaving] = useState();
  const location = useLocation();
  const backLocation = location && location.state && location.state.backLocation;
  const managedAssetsMinAllowed = session.profile.managedAssetsMinAllowed;
  const {data: existingAccount} = useSWR(
    accountID && `/crm/accounts?limit=1&id=${accountID}`,
    url => axiosInstance.get(url).then(res => res.data[0])
  );
  const {data: client} = useSWR(clientID && `/clients/${clientID}`);
  const {data: accountTypeData} = useSWR(`/accounts/types?${qs.stringify({
    managed: 1,
    sort: '+name',
    category: ['BANK', 'CHARITY', 'CORPORATE', 'EDUCATION', 'HEALTH', 'INVESTMENT', 'OTHER', 'REAL ESTATE', 'RETIREMENT'],
  })}`);
  const {data: assetTypes} = useSWR(
    'assetTypes',
    () => axiosInstance.get('/institutions/popular/categories')
      .then(res => {
        const categories = res.data;
        let assetCategoryID;

        for (let i = 0; i < categories.length; i++) {
          if (categories[i].name === 'Assets') {
            assetCategoryID = categories[i].id;
            break;
          }
        }

        if (!assetCategoryID) {
          return null;
        }

        return axiosInstance.get('/institutions/popular/categories/' + assetCategoryID + '/institutions')
          .then(res2 => res2.data.map(d => ({label: d.name, value: d.name})));
      })
  );
  const {data: historicalPositions} = useSWR(existingAccount
    && existingAccount.containerID
    && `/containers/${existingAccount.containerID}/positions?history=true&includeZeros=true`);

  const [positions, setPositions] = React.useState([]);

  const [account, setAccount] = useState({});

  const accountTypes = React.useMemo(
    () => accountTypeData && accountTypeData.map(d => ({label: d.name, value: d.name})),
    [accountTypeData]
  );

  log('rendering');

  React.useEffect(() => {
    if (existingAccount) {
      setAccount({
        ...existingAccount,
        institution: {title: existingAccount.institutionTitle, id: existingAccount.institutionID},
        type: {label: existingAccount.typeName, value: existingAccount.typeName},
      });
    }
  }, [existingAccount]);

  React.useEffect(() => {
    if (historicalPositions) {
      const todayDate = moment().format('YYYY-MM-DD');
      setPositions(historicalPositions.map(p => {
        const currentValue = {
          date: todayDate,
          price: p.price.amount,
          quantity: p.quantity,
          value: p.marketValue.amount,
        };
        const recentValue = {};
        if (p.history.length) {
          recentValue.date = p.history[p.history.length - 1].date;
          recentValue.price = p.history[p.history.length - 1].price;
          recentValue.quantity = p.history[p.history.length - 1].quantity;
          recentValue.value = p.history[p.history.length - 1].value;
        }
        if ((recentValue.price === currentValue.price)
          && (recentValue.quantity === currentValue.quantity)
          && (recentValue.value === currentValue.value)
        ) {
          return ({
            ...p,
            history: undefined,
            dates: p.history,
          });
        }
        return ({
          ...p,
          history: undefined,
          dates: [
            ...p.history,
            currentValue,
          ],
        });
      }));
    }
  }, [historicalPositions]);

  const positionCurrent = React.useMemo(() => positions.reduce((base, pos) => {
    if (pos.dates) {
      if (!pos.dates.length) {
        delete base[pos.id || pos.tid];
      } else {
        const h = pos.dates.sort((a, b) => a.date > b.date ? -1 : 1)[0];
        base[pos.id || pos.tid] = {...h, value: (h.price * h.quantity) || 0};
      }
    }
    return base;
  }, {}), [positions]);

  const totalValue = React.useMemo(() => Object.values(positionCurrent).reduce((base, p) => base + p.value, 0));

  const updateAccount = (name, value) => {
    setAccount(current => ({
      ...current,
      [name]: value,
    }));
  };

  const submit = event => {
    event.preventDefault();
    setSaving(true);
    axiosInstance.post(`/clients/${clientID || account.clientID}/assets`, [
      {
        id: account.id || undefined,
        name: account.name,
        number: account.number,
        type: account.type.value,
        value: Math.round(totalValue * 100) / 100,
        institutionID: account.institution.id,
        positions: positions,
      },
    ])
      .then(res => history.push(`/analyze/dashboard?${buildQueryContext({
        containerID: res.data[0].containerID,
        name: res.data[0].name,
        id: res.data[0].id,
        type: 'ACCOUNT',
      })}`))
      .catch(err => setNotification({message: parseErrorMessage(err)}))
      .finally(() => setSaving(false));
  };

  const assetInput = position => {
    const updateAsset = (name, value) => {
      setPositions(currentPositions => {
        const currentPosition = currentPositions.find(c => (c.id || c.tid) === (position.id || position.tid));
        currentPositions[positions.findIndex(c => (c.id || c.tid) === (position.id || position.tid))] = {
          ...currentPosition,
          [name]: value,
        };
        return [...currentPositions];
      });
    };
    const updateAssetHistory = (date, name, value) => {
      setPositions(currentPositions => {
        const newDate = {...date, [name]: value};
        const currentPosition = currentPositions.find(c => (c.id || c.tid) === (position.id || position.tid));
        newDate.value = newDate.price * newDate.quantity;
        currentPosition.dates[currentPosition.dates.findIndex(h => h.date === date.date)] = {...newDate};
        currentPositions[currentPositions.findIndex(c => (c.id || c.tid) === (currentPosition.id || currentPosition.tid))] = {
          ...currentPosition,
          dates: [...currentPosition.dates],
        };
        return [...currentPositions];
      });
    };
    const removeRender = row => <Icon svg={'remove'}
                                      action={() => updateAsset(
                                        'dates',
                                        position.dates.filter(h => h.date !== row.date)
                                      )}/>;
    const valueRender = row => <NumberInput placeholder={'$0.00'}
                                            type={'currency'}
                                            decimalPlaces={2}
                                            required={true}
                                            readOnly={true}
                                            dataPoint={{name: 'value', value: row.value}}/>;
    const quantityRender = row => <NumberInput placeholder={'0'}
                                               type={'number'}
                                               decimalPlaces={2}
                                               required={true}
                                               onBlur={(name, value) => updateAssetHistory(row, name, value)}
                                               dataPoint={{name: 'quantity', value: row.quantity}}/>;
    const priceRender = row => <NumberInput placeholder={'$0.00'}
                                            decimalPlaces={4}
                                            type={'currency'}
                                            required={true}
                                            onBlur={(name, value) => updateAssetHistory(row, name, value)}
                                            dataPoint={{name: 'price', value: row.price}}/>;
    return <Column>
      {!position.id && <Row justifySubRow={'right'}>
        <Button importance={'secondary'}
                width={'auto'}
                size={'sm'}
                label={'Remove Asset'}
                action={() => setPositions(positions.filter(p => p !== position))}/>
      </Row>}
      <Row wrap={true}>
        <TextInput readOnly={!!position.securityID}
                   type={'Text'}
                   width={'125px'}
                   layout={{grow: 1}}
                   title={'Asset Name'}
                   placeholder={'Asset Name'}
                   onChange={updateAsset}
                   required={true}
                   dataPoint={{name: 'name', value: position.name}}/>
        <Dropdown dataPoint={{name: 'type', value: position.type && {label: position.type, value: position.type}}}
                  onChange={(name, value) => updateAsset(name, value && value.value)}
                  placeholder={'Asset Type'}
                  isSearchable={true}
                  isClearable={false}
                  disabled={!!position.securityID}
                  title={'Asset Type'}
                  width={'125px'}
                  options={assetTypes}
                  layout={{grow: 1}}
                  required={true}
                  isCreatable={false}/>
        <TextInput readOnly={!!position.securityID}
                   type={'Text'}
                   width={'125px'}
                   layout={{grow: 1}}
                   onChange={updateAsset}
                   title={'Symbol (optional)'}
                   placeholder={'Ticker'}
                   dataPoint={{name: 'ticker', value: position.ticker}}/>
        <TextInput readOnly={!!position.securityID}
                   type={'Text'}
                   width={'125px'}
                   onChange={updateAsset}
                   layout={{grow: 1}}
                   title={'Cusip (optional)'}
                   placeholder={'Cusip'}
                   dataPoint={{name: 'cusip', value: position.cusip}}/>
      </Row>
      <TextArea title={'Description'}
                readOnly={!!position.securityID}
                onChange={updateAsset}
                required={false}
                placeholder={'Description'}
                height={'30px'}
                isRichEditor={false}
                dataPoint={{name: 'description', value: position.description}}/>
      {account.type && (account.type.value || account.type.name) === 'Annuity' && <Row>
        <DatePicker title={'Guaranteed Living Benefit Date'}
                    onChange={updateAsset}
                    placeholder={'Guaranteed Living Benefit Date'}
                    dataPoint={{name: 'guaranteedLivingBenefitAsOfDate', value: position.guaranteedLivingBenefitAsOfDate}}/>
        <NumberInput type={'currency'}
                     placeholder={'$0.00'}
                     width={'125px'}
                     layout={{grow: 1}}
                     title={'Guaranteed Living Benefit Amount'}
                     onBlur={updateAsset}
                     dataPoint={{name: 'guaranteedLivingBenefitAmount', value: position.guaranteedLivingBenefitAmount}}/>
        <DatePicker title={'Guaranteed Death Benefit Date'}
                    onChange={updateAsset}
                    placeholder={'Guaranteed Death Benefit Date'}
                    dataPoint={{name: 'guaranteedDeathBenefitAsOfDate', value: position.guaranteedDeathBenefitAsOfDate}}/>
        <NumberInput type={'currency'}
                     placeholder={'$0.00'}
                     width={'125px'}
                     layout={{grow: 1}}
                     title={'Guaranteed Death Benefit Amount'}
                     onBlur={updateAsset}
                     dataPoint={{name: 'guaranteedDeathBenefitAmount', value: position.guaranteedDeathBenefitAmount}}/>
      </Row>}
      <Column width={'100%'}>
        <Row wrap={true} width={'100%'}>
          <NumberInput readOnly={true}
                       type={'currency'}
                       decimalPlaces={2}
                       width={'125px'}
                       layout={{grow: 1}}
                       title={'Asset Value'}
                       placeholder={formatter('currency')(
                         positionCurrent[position.id || position.tid] && positionCurrent[position.id || position.tid].value || 0
                       )}
                       dataPoint={{
                         name: 'value',
                         value: position.addPrice ? position.addPrice * position.addQuantity : '',
                       }}/>
          <NumberInput type={'currency'}
                       decimalPlaces={2}
                       width={'125px'}
                       layout={{grow: 1}}
                       title={'Price'}
                       onBlur={updateAsset}
                       placeholder={formatter('currency')(
                         positionCurrent[position.id || position.tid] && positionCurrent[position.id || position.tid].price || 0
                       )}
                       dataPoint={{name: 'addPrice', value: position.addPrice}}/>
          <NumberInput type={'number'}
                       width={'125px'}
                       decimalPlaces={2}
                       layout={{grow: 1}}
                       title={'Quantity'}
                       placeholder={positionCurrent[position.id || position.tid] && positionCurrent[position.id
                       || position.tid].quantity || 0}
                       onBlur={updateAsset}
                       dataPoint={{name: 'addQuantity', value: position.addQuantity}}/>
          <DatePicker title={'Date'}
                      onChange={updateAsset}
                      placeholder={'Valuation Date'}
                      minDate={moment().subtract(managedAssetsMinAllowed.value, managedAssetsMinAllowed.unit).format('YYYY-MM-DD')}
                      maxDate={moment().format('YYYY-MM-DD')}
                      dataPoint={{name: 'addDate', value: position.addDate}}/>
          <Button importance={'primary'}
                  size={'sm'}
                  label={'Add Value'}
                  disabled={!(position.addQuantity !== null && position.addPrice !== null && position.addDate)}
                  action={() => {
                    const idx = position.dates && position.dates.findIndex(h => h.date === position.addDate);
                    if (position.dates && ~idx) {
                      position.dates[idx] = {
                        price: position.addPrice,
                        value: position.addPrice * position.addQuantity,
                        quantity: position.addQuantity,
                        date: position.addDate,
                      };
                      updateAsset('dates', [...position.dates]);
                    } else {
                      updateAsset(
                        'dates',
                        (position.dates || []).concat([
                          {
                            price: position.addPrice,
                            value: position.addPrice * position.addQuantity,
                            quantity: position.addQuantity,
                            date: position.addDate,
                          },
                        ])
                      );
                    }
                    updateAsset('addPrice', '');
                    updateAsset('addQuantity', '');
                    updateAsset('addDate', '');
                    updateAsset('showHistory', true);
                  }}/>
          <Button importance={'secondary'}
                  size={'sm'}
                  disabled={!(position.dates && position.dates.length)}
                  label={`${position.showHistory ? 'Hide' : 'Show'} History`}
                  action={() => updateAsset('showHistory', !position.showHistory)}/>
        </Row>
        {position.showHistory && <Table
          defaultSort={{column: 'date', desc: true}}
          dataSet={position.dates}
          columns={[
            {
              title: 'Date',
              dataPoint: 'date',
              alignment: 'left',
              type: 'date',
              minWidth: 50,
              isSortable: false,
            },
            {
              title: 'Price',
              dataPoint: 'price',
              type: 'custom',
              render: priceRender,
              minWidth: 100,
              isSortable: false,
            },
            {
              title: 'Quantity',
              dataPoint: 'quantity',
              type: 'custom',
              render: quantityRender,
              minWidth: 100,
              isSortable: false,
            },
            {
              title: 'Value',
              dataPoint: 'value',
              type: 'custom',
              minWidth: 100,
              render: valueRender,
              isSortable: false,
            },
            {
              title: 'Remove',
              dataPoint: 'id',
              type: 'custom',
              minWidth: 5,
              render: removeRender,
              isSortable: false,
            },
          ]}
        />}
      </Column>
    </Column>;
  };

  const addAsset = () => {
    openModal({
      type: 'AddManagedAssetModal',
      onSuccess: sec => {
        let newAsset = {tid: uuid()};
        if (sec) {
          sec.securityID = sec.id;
          sec.type = sec.classifications && sec.classifications.assetClasses && sec.classifications.assetClasses[0]
            ? sec.classifications.assetClasses[0].name
            : null;
          delete sec.id;
          newAsset = {...newAsset, ...sec};
        }
        setPositions([...positions, newAsset]);
      },
    });
  };

  const addInstitution = () => openModal({
    type: 'AddInstitutionModal',
    onSuccess: institution => {
      setAccount({...account, institution});
    },
  });

  if (!historicalPositions && !client) {
    return <LoadingSpinner/>;
  }

  return <Page>
    <Tile key='mangedAssets' title={`${!!clientID ? 'Create' : 'Edit'} Assets`}>
      <form id={'managedForm'} onSubmit={submit}>
        <Column showDividers={true} width={'100%'}>
          <Column width={'100%'}>
            <Row wrap={true} justifySubRow={'space between'}>
              <TextInput readOnly={true}
                         type={'Text'}
                         width={'125px'}
                         layout={{grow: 1}}
                         title={'Client'}
                         placeholder={'Client Name'}
                         dataPoint={{name: 'clientName', value: (client && client.displayName) || account.clientDisplayName}}/>
              <TextInput readOnly={true}
                         type={'Text'}
                         width={'125px'}
                         layout={{grow: 1}}
                         title={'Household'}
                         placeholder={'Household Name'}
                         dataPoint={{name: 'householdName', value: (client && client.householdName) || account.householdName}}/>
              <NumberInput readOnly={true}
                           type={'currency'}
                           required={true}
                           width={'125px'}
                           layout={{grow: 1}}
                           title={'Account Value'}
                           placeholder={'$0.00'}
                           dataPoint={{name: 'accountValue', value: totalValue}}/>
              <Row justifySubRow={'right'} width={'125px'} layout={{grow: 1}}>
                {backLocation && <Button importance={'secondary'}
                                         size={'md'}
                                         label={'Cancel'}
                                         width={'auto'}
                                         action={() => history.push(backLocation)}/>}
                <Button importance={'primary'} size={'md'} processing={saving} label={'Save'} action={'SUBMIT'}/>
              </Row>
            </Row>
            <Row wrap={true} alignItems={'top'} justifySubRow={'space between'}>
              <TextInput type={'Text'}
                         width={'125px'}
                         required={true}
                         layout={{grow: 1}}
                         title={'Account Name'}
                         placeholder={'Account Name'}
                         onChange={updateAccount}
                         dataPoint={{name: 'name', value: account.name}}/>
              <TextInput type={'Text'}
                         required={true}
                         width={'125px'}
                         layout={{grow: 1}}
                         title={'Account Number'}
                         placeholder={'Account Number'}
                         onChange={updateAccount}
                         dataPoint={{name: 'number', value: account.number}}/>
              <Dropdown dataPoint={{name: 'type', value: account.type}}
                        onChange={updateAccount}
                        placeholder={'Account Type'}
                        isSearchable={true}
                        required={true}
                        isClearable={false}
                        title={'Account Type'}
                        width={'125px'}
                        options={accountTypes}
                        layout={{grow: 1}}
                        isCreatable={false}/>
              {isEntitled([{group: 'account', feature: 'institution', access: 'r'}])
              && <Column layout={{grow: 1}} width={'125px'}>
                <Dropdown dataPoint={{
                  name: 'institution',
                  value: account.institution ? {label: account.institution.title, value: account.institution} : null,
                }}
                          onChange={(name, value) => updateAccount(name, value && value.value || undefined)}
                          placeholder={'Institution Name'}
                          isSearchable={true}
                          isClearable={false}
                          width={'fill'}
                          required={true}
                          title={'Institution'}
                          dataFunction={gatherInstitutions}
                          isCreatable={false}/>
                {clientID && <DescriptiveText margin={'0 5px 5px 5px'} alignment={'left'}>
                  <LinkedText action={addInstitution} fontFamily={theme.bodyText.name} fontSize={'10px'}>Don&#39;t see your
                    institution?</LinkedText>
                </DescriptiveText>}
              </Column>}
            </Row>
          </Column>
          {positions.map(assetInput)}
          <Button importance={'secondary'} size={'md'} label={'Add Asset'} action={addAsset}/>
        </Column>
      </form>
    </Tile>
  </Page>;
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = dispatch => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
  setNotification: notification => dispatch(_setNotification(notification)),
});
export default withTheme(connect(mapStateToProps, mapDispatchToProps)(Managed));

Managed.propTypes = {
  session: PropTypes.object,
  openModal: PropTypes.func.isRequired,
  setNotification: PropTypes.func.isRequired,
  theme: PropTypes.object,
};
